/** Database schema, view and value types (see ADR 0005). */

export type PropertyType =
  | 'title'
  | 'text'
  | 'number'
  | 'select'
  | 'multi_select'
  | 'status'
  | 'date'
  | 'person'
  | 'files'
  | 'checkbox'
  | 'url'
  | 'email'
  | 'phone'
  | 'formula'
  | 'relation'
  | 'rollup'
  | 'created_time'
  | 'created_by'
  | 'last_edited_time'
  | 'last_edited_by'
  | 'unique_id'
  | 'button';

export type OptionColor = 'default' | 'gray' | 'brown' | 'orange' | 'yellow' | 'green' | 'blue' | 'purple' | 'pink' | 'red';
export const OPTION_COLORS: OptionColor[] = ['default', 'gray', 'brown', 'orange', 'yellow', 'green', 'blue', 'purple', 'pink', 'red'];

export interface SelectOption {
  id: string;
  name: string;
  color: OptionColor;
  /** Status options only: which group the option belongs to. */
  group?: 'todo' | 'in_progress' | 'complete';
}

export type NumberFormat =
  | 'number'
  | 'number_with_commas'
  | 'percent'
  | 'dollar'
  | 'euro'
  | 'pound'
  | 'yen'
  | 'rupee'
  | 'won'
  | 'yuan'
  | 'franc'
  | 'real'
  | 'krona'
  | 'ruble'
  | 'canadian_dollar'
  | 'australian_dollar';

export type CalcFn =
  | 'none'
  | 'show_original'
  | 'show_unique'
  | 'count_all'
  | 'count_values'
  | 'count_unique'
  | 'count_empty'
  | 'count_not_empty'
  | 'percent_empty'
  | 'percent_not_empty'
  | 'sum'
  | 'average'
  | 'median'
  | 'min'
  | 'max'
  | 'range'
  | 'earliest_date'
  | 'latest_date'
  | 'date_range'
  | 'checked'
  | 'unchecked'
  | 'percent_checked'
  | 'percent_unchecked';

export interface ButtonAction {
  kind: 'insert_blocks' | 'add_page' | 'edit_pages' | 'open_page' | 'confirm' | 'notify' | 'open_url';
  /** insert_blocks: markdown inserted below the button. */
  markdown?: string;
  /** add_page / edit_pages: target database. */
  dbId?: string;
  /** add_page / edit_pages: property values to set (template-style relative values allowed). */
  values?: Record<string, unknown>;
  /** open_page: page to open. */
  pageId?: string;
  /** confirm: the question (stops the button when answered No); notify: the message (P-955). */
  message?: string;
  /** notify: member ids; "@me" is the person who clicked. */
  people?: string[];
  /** open_url: an http(s) or mailto address. */
  url?: string;
}

export interface PropertyDef {
  id: string;
  name: string;
  type: PropertyType;
  options?: SelectOption[];
  /** number */
  format?: NumberFormat;
  decimals?: number | null;
  showAs?: 'number' | 'bar' | 'ring';
  divideBy?: number;
  /** status: show as a select (default) or as a checkbox */
  statusAs?: 'select' | 'checkbox';
  /** date */
  dateFormat?: 'relative' | 'full' | 'mdy' | 'dmy' | 'ymd';
  timeFormat?: '12h' | '24h';
  timeZone?: string;
  /** person */
  limit?: 'one' | 'many';
  /** relation */
  relation?: { dbId: string; twoWay: boolean; syncedPropId?: string; limit: 'one' | 'many' };
  /** rollup */
  rollup?: { relationPropId: string; targetPropId: string; calc: CalcFn };
  /** formula */
  expression?: string;
  /** unique_id */
  prefix?: string;
  /** button */
  button?: { label: string; actions: ButtonAction[] };
}

export type Layout = 'table' | 'board' | 'list' | 'gallery' | 'calendar' | 'timeline' | 'chart';

export type FilterOperator =
  | 'is'
  | 'is_not'
  | 'contains'
  | 'not_contains'
  | 'starts_with'
  | 'ends_with'
  | 'is_empty'
  | 'is_not_empty'
  | 'eq'
  | 'neq'
  | 'gt'
  | 'lt'
  | 'gte'
  | 'lte'
  | 'before'
  | 'after'
  | 'on_or_before'
  | 'on_or_after'
  | 'within';

export interface FilterRule {
  kind: 'rule';
  propId: string;
  op: FilterOperator;
  /** Comparison value. Dates: ISO date, or a relative token ('today', 'tomorrow', 'yesterday', 'one_week_ago', ...).
   * `within`: 'past_week' | 'past_month' | 'past_year' | 'next_week' | 'next_month' | 'next_year'. Person: user id or 'me'. */
  value?: unknown;
}

export interface FilterGroup {
  kind: 'group';
  conj: 'and' | 'or';
  rules: (FilterRule | FilterGroup)[];
}

export interface SortRule {
  propId: string;
  dir: 'asc' | 'desc';
}

export interface GroupBy {
  propId: string;
  /** Manual order of group keys; otherwise alphabetical/option order. */
  order?: 'manual' | 'asc' | 'desc';
  manual?: string[];
  hidden?: string[];
  collapsed?: string[];
  hideEmpty?: boolean;
  /** Date grouping granularity. */
  dateBy?: 'relative' | 'day' | 'week' | 'month' | 'year';
}

/** A chart view (P-959): rows grouped by a property, measured per group (bars, line, donut) or in total (number). */
export interface ChartConfig {
  type: 'bar' | 'line' | 'donut' | 'number';
  /** X axis or slices; null for a number. */
  groupBy: string | null;
  /** Dates group by month unless set. */
  dateBy?: 'day' | 'week' | 'month' | 'year';
  measure: { fn: 'count' } | { fn: 'sum' | 'average' | 'min' | 'max' | 'median'; propId: string };
  /** Group order ('key'), or by value. */
  sort?: 'key' | 'value-desc' | 'value-asc';
  /** Leave out the "No <property>" group and groups without rows. */
  hideEmpty?: boolean;
}

/** Conditional colour (P-958): rows, or one property's cells, matching `when` get `color`; the first rule wins. */
export interface ColorRule {
  id: string;
  /** 'row' (the row or card), or a property id (its cells). */
  target: string;
  color: OptionColor;
  when: FilterGroup;
}

export interface ViewDef {
  id: string;
  name: string;
  layout: Layout;
  filter: FilterGroup | null;
  sorts: SortRule[];
  groupBy: GroupBy | null;
  subGroupBy?: GroupBy | null;
  /** Visible properties in display order (title always shown first in table). */
  visible: string[];
  widths: Record<string, number>;
  wrapAll?: boolean;
  wrapProps?: string[];
  freezeUpTo?: string | null;
  calcs: Record<string, CalcFn>;
  loadLimit: 10 | 25 | 50 | 100;
  openIn: 'side' | 'center' | 'full';
  card?: { preview: 'none' | 'content' | 'cover' | 'files'; size: 'small' | 'medium' | 'large'; fitImage?: boolean };
  calendarBy?: string | null;
  showWeekends?: boolean;
  timeline?: { startProp: string | null; endProp: string | null; zoom: 'hours' | 'day' | 'week' | 'biweek' | 'month' | 'quarter' | 'year'; showTable: boolean };
  defaultTemplate?: string | null;
  /** Conditional colour rules (P-958), in order. */
  colors?: ColorRule[];
  /** Chart layout settings (P-959). */
  chart?: ChartConfig;
}

/** A date property value. */
export interface DateValue {
  start: string;
  end?: string | null;
  time?: string | null;
  endTime?: string | null;
  reminder?: string | null;
}

/** A file in a files & media value. */
export interface FileValue {
  name: string;
  url: string;
  type?: string;
}

/** What property logic needs about a row (decoupled from Yjs). */
export interface RowData {
  id: string;
  title: string;
  props: Record<string, unknown>;
  createdAt: number;
  createdBy: string;
  editedAt: number;
  editedBy: string;
}
